import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { USER_ROLE_LABELS } from '@fsm/shared';
import { useAuth } from '../lib/auth';
import { Avatar, Button, cx } from './ui';
import { Icon, type IconName } from './icons';
import { GlobalSearch } from './GlobalSearch';
import { Paywall } from './Paywall';
import { ScreenPop } from './ScreenPop';
import { Softphone } from './Softphone';
import { useCalls } from '../lib/calls';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  officeOnly?: boolean;
  /** Crewlark staff only. The server 404s the route for anybody else. */
  ownerOnly?: boolean;
  /** Only for a company that has the phone. The server 403s it otherwise. */
  telephonyOnly?: boolean;
}

/*
 * Two groups, because they are two jobs.
 *
 * Workspace is what today is made of — the board, the work on it, the people
 * ringing about it, and the paper that work turns into. Business is what you
 * open once a week. Anyone who spends a shift in here lives in the first
 * group; splitting it means their six destinations sit together at the top
 * instead of being the first six of nine.
 */
const NAV: Array<{ label: string; items: NavItem[] }> = [
  {
    label: 'Workspace',
    items: [
      { to: '/dispatch', label: 'Dispatch', icon: 'grid' },
      { to: '/jobs', label: 'Jobs', icon: 'job' },
      { to: '/calls', label: 'Calls', icon: 'phone', officeOnly: true, telephonyOnly: true },
      { to: '/customers', label: 'Customers', icon: 'users' },
      { to: '/estimates', label: 'Estimates', icon: 'file', officeOnly: true },
      { to: '/invoices', label: 'Invoices', icon: 'cost', officeOnly: true },
    ],
  },
  {
    label: 'Business',
    items: [
      { to: '/reports', label: 'Reports', icon: 'chart', officeOnly: true },
      { to: '/pricebook', label: 'Price book', icon: 'book', officeOnly: true },
      { to: '/settings', label: 'Settings', icon: 'settings', officeOnly: true },
    ],
  },
  /*
   * Crewlark's own. A group of its own rather than an item in Business,
   * because mixing "every company on the platform" in with this company's
   * price book is how somebody clicks it in front of a customer.
   */
  {
    label: 'Crewlark',
    items: [{ to: '/admin', label: 'Companies', icon: 'users', ownerOnly: true }],
  },
];

const ALL_ITEMS = NAV.flatMap((group) => group.items);

/**
 * The last crumb in the top bar.
 *
 * A record page knows what it is called; the shell around it does not, and the
 * URL only carries an id. So a page announces its own name and the bar reads
 * it — rather than the bar guessing, or every page drawing its own breadcrumb
 * and the real one sitting empty.
 */
const CrumbContext = createContext<((label: string | null) => void) | null>(null);

export function useCrumb(label: string | null): void {
  const set = useContext(CrumbContext);
  useEffect(() => {
    set?.(label);
    return () => set?.(null);
  }, [set, label]);
}

export function Layout({ initialCrumb = null }: { initialCrumb?: string | null }) {
  const { user, company, signOut, can } = useAuth();
  const { liveCalls } = useCalls();
  const location = useLocation();
  const isOffice = can('admin', 'dispatcher');

  /*
   * `initialCrumb` exists for the render harness, which paints one frame and
   * never runs an effect. In the browser it is always null and the value
   * arrives from the page a moment later.
   */
  const [crumb, setCrumb] = useState<string | null>(initialCrumb);
  const section = ALL_ITEMS.find((item) => location.pathname.startsWith(item.to));

  return (
    <div className="flex h-full">
      {/*
       * A white rail on a near-white page, separated by a hairline. The active
       * item is the single green thing in the navigation, so "you are here" is
       * the first thing the eye finds.
       */}
      <nav className="flex w-[222px] shrink-0 flex-col border-r border-line bg-surface px-4 pt-6">
        <div className="flex items-center gap-2.5 px-2">
          <span
            className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-accent font-display text-[19px] font-extrabold tracking-[-0.07em] text-white"
            aria-hidden
          >
            {(company?.name ?? 'F').trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 font-display text-[16px] leading-[1.25] font-extrabold tracking-[-0.03em]">
            {company?.name ?? 'Field Service'}
          </div>
        </div>

        <GlobalSearch />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {NAV.map((group) => {
            const items = group.items.filter(
              (item) =>
                (!item.officeOnly || isOffice) &&
                (!item.ownerOnly || user?.platformOwner === true) &&
                /*
                 * Most companies have no phone, and a Calls tab that opens on
                 * an empty screen reads as a broken feature rather than one
                 * they were never sold. The server refuses these routes too —
                 * see auth/telephony-enabled.ts. This is only the courtesy.
                 */
                (!item.telephonyOnly || company?.telephonyEnabled === true),
            );
            if (items.length === 0) return null;
            return (
              <div key={group.label} className="mb-7">
                <div className="mb-2.5 pl-3 text-[12px] font-semibold tracking-[0.12em] text-ink-4 uppercase">
                  {group.label}
                </div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cx(
                        'my-[3px] flex min-h-[44px] items-center gap-3 rounded-[7px] px-3 py-2.5 text-[14px] transition-colors',
                        isActive
                          ? 'bg-accent-soft font-semibold text-accent'
                          : 'font-medium text-ink-3 hover:bg-raised hover:text-accent',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon name={item.icon} size={18} />
                        {item.label}
                        {item.to === '/calls' && liveCalls.length > 0 ? (
                          <span className="ml-auto grid size-[18px] place-items-center rounded-full bg-accent text-[10px] font-bold text-white">
                            {liveCalls.length}
                          </span>
                        ) : (
                          isActive && (
                            <span className="ml-auto size-1.5 rounded-full bg-accent" aria-hidden />
                          )
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>

        <div className="mt-auto">
          <div className="flex items-center gap-2 px-2 pt-5 pb-4 text-[12px] text-ink-4">
            <span className="size-1.5 rounded-full bg-good" aria-hidden />
            {company?.name ? `${company.name} workspace` : 'Workspace'}
          </div>
          <div className="flex items-center gap-2.5 border-t border-line py-4">
            <Avatar name={user?.name ?? '?'} color={user?.colorHex ?? '#225d4d'} size={36} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold">{user?.name}</div>
              <div className="text-[12px] text-ink-4">
                {user ? USER_ROLE_LABELS[user.role] : ''}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="px-2 text-ink-4"
              title="Sign out"
              onClick={() => void signOut()}
            >
              <Icon name="arrow" size={15} />
              <span className="sr-only">Sign out</span>
            </Button>
          </div>
        </div>
      </nav>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/*
         * Above the header, not inside the scrolling area: a notice about
         * money that scrolls away has been read once and forgotten. Renders
         * nothing at all for a company in good standing.
         */}
        <Paywall />

        {/*
         * Where you are, and whose data this is. The company name is here on
         * every screen, which matters the moment somebody runs two of these
         * side by side.
         */}
        <header className="flex h-[70px] shrink-0 items-center justify-between gap-6 border-b border-line bg-surface px-[30px]">
          <nav className="flex min-w-0 items-center gap-3.5 text-[13px] text-ink-3">
            {section ? (
              <>
                <Icon name={section.icon} size={17} className="text-ink-4" />
                <NavLink to={section.to} className="transition-colors hover:text-accent">
                  {section.label}
                </NavLink>
                {crumb && (
                  <>
                    <Icon name="chevron" size={12} className="text-line-2" />
                    <span className="truncate font-medium text-ink-2">{crumb}</span>
                  </>
                )}
              </>
            ) : (
              <span className="font-medium text-ink-2">{company?.name}</span>
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-2.5 text-[13px] text-ink-3">
            <span className="size-[5px] rounded-full bg-good" aria-hidden />
            <span className="max-w-[240px] truncate">{company?.name}</span>
            <Avatar
              name={user?.name ?? '?'}
              color={user?.colorHex ?? '#225d4d'}
              size={29}
              className="ml-2"
            />
          </div>
        </header>

        <CrumbContext.Provider value={setCrumb}>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas">
            <Outlet />
          </div>
        </CrumbContext.Provider>
      </main>

      {isOffice && company?.telephonyEnabled === true && <ScreenPop />}
      {isOffice && company?.telephonyEnabled === true && <Softphone />}
    </div>
  );
}

/**
 * The line under the content: what this workspace is, and what you are looking
 * at. It is the quietest thing on the page and it is there for one reason —
 * printed or screenshotted, a page from here should still say where it is from.
 */
export function PageFooter({ label }: { label?: ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-[1860px] items-center justify-between px-[30px] pt-2 pb-6 text-[12px] text-ink-4">
      <span className="flex items-center gap-2">
        <Icon name="lock" size={13} />
        Internal workspace
      </span>
      {label && <span className="truncate">{label}</span>}
    </div>
  );
}
